import { PLAN_SELECTION_NOT_PERMITTED_CODE } from '@cadstrom/backend/shared';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import { Loading } from '#components/loading.js';
import { Toast } from '#components/toast/toast.js';
import { useTRPC } from '#src/trpc.js';
import { useAddToast, useToastStore } from '#state/stores/toast-store.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { ActiveClaimBanner } from './active-claim-banner.js';
import { EligiblePlansGeneratorFilter } from './eligible-plans-generator-filter.js';
import { EligiblePlansTable } from './eligible-plans-table.js';

const searchSchema = z.object({
	/** Generator whose plans to list; omitted lists every task type. */
	generator: z.string().optional(),
});

export const Route = createFileRoute('/_authorization-gate/wizard/queue')({
	validateSearch: searchSchema,
	staticData: {
		authorize: (ability) => ability.can('manage', 'wizardAdmin') || ability.can('manage', 'planSelector'),
	},
	beforeLoad: ({ context: { trpc }, search: { generator } }) => {
		// The task-type filter is applied by the server so the list stays the exact eligibility set;
		// only the MPN search in the component narrows client-side. `select` narrows to the success
		// member (or redirects) so the component sees a guaranteed listing.
		return {
			eligiblePlansOptions: trpc.wizard.step.listEligiblePlans.queryOptions(
				generator === undefined ? {} : { generatorName: generator },
				{
					select: (data) => {
						if (!data.isSuccess) {
							if (data.errorCode === PLAN_SELECTION_NOT_PERMITTED_CODE) {
								throw redirect({ to: '/wizard' });
							}
							throw new Error(data.errorMessage);
						}
						return { plans: data.plans, activeClaim: data.activeClaim };
					},
				},
			),
		};
	},
	loader: async ({ context: { trpc, queryClient, eligiblePlansOptions } }) => {
		// Claims are eventually reflected here; fetch on every navigation into the page so a claim
		// taken (or released) elsewhere is never masked by the default 2-minute staleTime.
		const { isSuccess, errorCode } = await queryClient.fetchQuery({ ...eligiblePlansOptions, staleTime: 0 });
		if (!isSuccess && errorCode === PLAN_SELECTION_NOT_PERMITTED_CODE) {
			useToastStore.getState().addToast(
				<Toast severity='warning' title='You cannot browse the queue'>
					Your access no longer covers choosing which plan to work. You can still pick up the next available task.
				</Toast>,
			);
			// The link that brought them here came from an ability map fetched before the access was
			// withdrawn. Re-read it so the surfaces offering the queue stop offering it.
			await queryClient.invalidateQueries({ queryKey: trpc.user.getCurrent.queryKey() });
			throw redirect({ to: '/wizard' });
		}
	},
	head: () => ({ meta: [{ title: 'Browse queue • Cadstrom' }] }),
	component: WizardQueuePage,
	pendingComponent: Loading,
});

function WizardQueuePage() {
	const trpc = useTRPC();
	const addToast = useAddToast();
	const el = useDataElTagGenerator(WizardQueuePage);
	const { generator } = Route.useSearch();
	const navigate = Route.useNavigate();
	const { eligiblePlansOptions } = Route.useRouteContext();

	const { data } = useSuspenseQuery(eligiblePlansOptions);

	const [term, setTerm] = useState('');

	const filteredPlans = useMemo(() => {
		const needle = term.trim().toLowerCase();
		if (needle === '') {
			return data.plans;
		}
		return data.plans.filter((plan) => (plan.manufacturerPartNumber ?? '').toLowerCase().includes(needle));
	}, [data.plans, term]);

	const { mutate: releaseClaim, isPending: isReleasing } = useMutation(
		trpc.wizard.step.releaseClaim.mutationOptions({
			onSuccess: ({ isSuccess, errorMessage }) => {
				if (!isSuccess) {
					addToast(
						<Toast severity='error' title='Failed to release claim'>
							{errorMessage}
						</Toast>,
					);
					return;
				}
				// Hard reload so the page comes back with fully fresh claim/queue state.
				window.location.reload();
			},
			onError: (error) => {
				addToast(
					<Toast severity='error' title='Failed to release claim'>
						{error.message}
					</Toast>,
				);
			},
		}),
	);

	return (
		<div {...el()} className='flex min-h-0 grow flex-col gap-4 overflow-y-auto p-4'>
			<div className='flex flex-col gap-2'>
				<p className='text-xl font-semibold text-gray-a12'>Browse queue</p>
				<p className='text-gray-a11'>
					Tasks currently on your queue that you can claim, ordered by priority. Begin any one.
				</p>
			</div>

			{data.activeClaim && (
				<ActiveClaimBanner
					activeClaim={data.activeClaim}
					onRelease={() => releaseClaim({})}
					isReleasing={isReleasing}
				/>
			)}

			<div className='flex flex-wrap items-center gap-2'>
				<TextField
					className='w-[400px] max-w-full'
					size='small'
					placeholder='Search by MPN...'
					value={term}
					onChange={(e) => setTerm(e.target.value)}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position='start'>
									<SearchIcon fontSize='small' />
								</InputAdornment>
							),
						},
					}}
				/>
				<EligiblePlansGeneratorFilter
					value={generator ?? null}
					onChange={(next) => void navigate({ search: (prev) => ({ ...prev, generator: next ?? undefined }) })}
				/>
			</div>

			<EligiblePlansTable plans={filteredPlans} activeClaimPlanId={data.activeClaim?.planId ?? null} />
		</div>
	);
}
